import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useStatsigClient } from '@statsig/react-bindings';

import {
  BarChart,
  Button,
  DEFAULT_CHART_COLORS,
  DownloadIcon,
  formatData,
  HelpIcon,
  Layout,
  OverlayTriggerTooltip,
  Select,
  Tabs,
  tooltipFormatter,
} from '@utilities';

import { QUESTION_TYPES } from '@api/instant_survey';

import { GATES } from '@constants';

import { computeProportionStatTest, statThresholds } from '@utilities/helpers/significance';

import { applySignificanceColumns } from './utilities/applySignificanceColumns';
import {
  computeMonadicSegmentSignificance,
  getMonadicSignificanceRowKey,
} from './utilities/monadicSignificanceAdapter';
import { buildProportionStatInput } from './utilities/significanceAdapter';

import ModalDownloadBestSets from '../../../../../../../../components/ModalDownloadBestSets';

import { DISPLAY_AS } from '../../utilities/helpers';

import ConceptImageList from '../ConceptImageList';
import { applyGridSignificanceColumns } from '../Grid/utilities/applyGridSignificanceColumns';
import ModalTop2Box from '../ModalTop2Box';
import OptionImageTable from '../OptionImageTable';
import OtherPleaseSpecifyTable from '../OtherPleaseSpecifyTable';
import SignificanceControls from '../SignificanceControls';

import { buildSortedBarChart, normalizeAnswerOptionChart } from './utilities/helpers';
import styles from './_index.module.scss';

const ComparisonCharts = ({
  compareByGroups,
  compareByValue = 'overall',
  conceptImages,
  data,
  displayAs = DISPLAY_AS.PERCENT.value,
  exportQuery,
  globalFilters,
  hideChart = false,
  initialSignificanceAlpha,
  isAnswerOptionFilteredChart = false,
  isAskWhySurvey = false,
  language,
  onOpenConceptImage,
  onOpenLightbox,
  onSignificanceChange,
  onTop2BoxApplied,
  question,
  questionOptions,
  renderKeyFindings,
  selectedAnswerOptions = [],
  significanceAvailable = false,
  sortByValue = 'default',
  sortOrder = 'default',
  survey,
}) => {
  const [isTop2BoxModalActive, setIsTop2BoxModalActive] = useState(false);
  const [isTurfAnalysis, setIsTurfAnalysis] = useState();
  const [selectedSegmentId, setSelectedSegmentId] = useState();
  const [isDownloadBestSetsModalOpen, setIsDownloadBestSetsModalOpen] = useState(false);
  const [significanceAlpha, setSignificanceAlpha] = useState(initialSignificanceAlpha);

  const turfData = data?.turfAnalysis;

  const { client } = useStatsigClient();
  const canTop2Box = !isAskWhySurvey && survey && question?.type === QUESTION_TYPES.RADIO.value;
  const significanceEnabled = client.checkGate(GATES.SURVEY_RESPONSES_SIGNIFICANCE);
  const showSignificanceLetters = significanceAvailable && significanceAlpha != null;
  const isMonadicSectionQuestion = Boolean(question?.concepts?.length);

  const handleSignificanceChange = (nextAlpha) => {
    setSignificanceAlpha(nextAlpha ?? undefined);
    onSignificanceChange?.(nextAlpha);
  };

  useEffect(() => {
    setIsTurfAnalysis((prev) => {
      if (turfData) return prev ?? true;
      return false;
    });
    if (!turfData) setSelectedSegmentId(undefined);
  }, [turfData]);

  const analysisOptions = [
    { label: 'TURF', value: true },
    { label: 'Standard', value: false },
  ];
  const selectedAnalysisOption =
    analysisOptions.find((option) => option.value === isTurfAnalysis) ?? analysisOptions[0];

  const chartToDisplay = displayAs === DISPLAY_AS.PERCENT.value ? data?.chartPct : data?.chartCount;
  const tableToDisplay = displayAs === DISPLAY_AS.PERCENT.value ? data?.tablePct : data?.tableCount;

  const selectedSegment = useMemo(
    () => turfData?.segments?.find(({ id }) => id === selectedSegmentId),
    [turfData?.segments, selectedSegmentId]
  );

  const activeChart = isTurfAnalysis ? selectedSegment?.chart : chartToDisplay;
  const activeTable = isTurfAnalysis ? selectedSegment?.table : tableToDisplay;
  const normalizedActiveChart = useMemo(
    () => normalizeAnswerOptionChart({ activeChart, selectedAnswerOptions }),
    [activeChart, selectedAnswerOptions]
  );

  /**
   * Significance letters. Always computed from tableCount (raw
   * counts + per-segment base n), independent of the Count/Percentage display
   * mode. Skipped only while the TURF analysis VIEW is active — those segment
   * tables are out of scope for the first cut. Switching a TURF-capable question
   * to Standard analysis renders an ordinary radio/checkbox comparison table,
   * which IS in scope and is annotated normally.
   *
   * Non-null when significance is enabled AND the table is structurally
   * comparable (`buildProportionStatInput` found ≥2 segment columns). `result`
   * is null when the test itself is suppressed (e.g. every compared segment has
   * fewer than 30 respondents): no letters render, but the legend still shows to
   * explain the absence. When the table can't support a comparison at all
   * (`built` is null) the whole memo is null, so the legend doesn't dangle.
   *
   * Monadic-section questions letter compare-by segments against each other within
   * each concept column (`monadic`), and are never lettered concept-vs-concept:
   * sequential monadic respondents see every concept, so those samples overlap.
   */
  const significance = useMemo(() => {
    if (!significanceEnabled || !showSignificanceLetters || isTurfAnalysis) return null;
    if (isMonadicSectionQuestion) {
      const monadic = computeMonadicSegmentSignificance(data?.tableCount, significanceAlpha);
      return monadic ? { monadic } : null;
    }
    const built = buildProportionStatInput(data?.tableCount);
    if (!built) return null;
    return {
      result: computeProportionStatTest(built.input, significanceAlpha),
      segmentAccessors: built.segmentAccessors,
      statRowByKey: built.statRowByKey,
    };
  }, [
    significanceEnabled,
    showSignificanceLetters,
    significanceAlpha,
    isTurfAnalysis,
    isMonadicSectionQuestion,
    data?.tableCount,
  ]);

  const tableColumns = useMemo(() => {
    if (significance?.monadic) {
      return applyGridSignificanceColumns({
        columns: activeTable?.columns,
        getRowKey: getMonadicSignificanceRowKey,
        significance: significance.monadic,
      });
    }
    return significance?.result
      ? applySignificanceColumns({
          columns: activeTable?.columns,
          segmentAccessors: significance.segmentAccessors,
          statRowByKey: significance.statRowByKey,
          statTest: significance.result,
        })
      : activeTable?.columns;
  }, [significance, activeTable]);

  const colors = useMemo(() => {
    const series = normalizedActiveChart?.series;
    if (!compareByGroups?.length || !series?.length) return DEFAULT_CHART_COLORS;

    let unusedColorIndex = 0;
    const unusedColors = DEFAULT_CHART_COLORS;

    return series.map(({ id: seriesId, name: seriesName }) => {
      const matchingGroup = compareByGroups?.find(
        ({ id: groupId, name: groupName }) => groupId === seriesId || groupName === seriesName
      );
      return matchingGroup?.color || unusedColors[unusedColorIndex++ % unusedColors.length];
    });
  }, [compareByGroups, normalizedActiveChart?.series]);

  const isOverall = compareByValue === 'overall';
  const hasSortBySelection = sortByValue !== 'default';

  const hasNumericXPoints = useMemo(() => {
    const series = normalizedActiveChart?.series;
    if (!Array.isArray(series)) return false;

    return series.some((s) => {
      const dataArr = Array.isArray(s?.data) ? s.data : [];
      return dataArr.some((pt) => {
        if (Array.isArray(pt)) return typeof pt[0] === 'number'; // [x, y]
        return pt && typeof pt === 'object' && typeof pt.x === 'number'; // { x, y }
      });
    });
  }, [normalizedActiveChart?.series]);

  const canApplySorting = isOverall ? true : hasSortBySelection;
  const effectiveSortOrder = canApplySorting && !hasNumericXPoints ? sortOrder : 'default';

  const { chartPlotOptions, chartSeries, chartXAxis } = useMemo(
    () =>
      buildSortedBarChart({
        activeChart: normalizedActiveChart,
        compareByGroups,
        sortByValue,
        sortOrder: effectiveSortOrder,
      }),
    [compareByGroups, effectiveSortOrder, normalizedActiveChart, sortByValue]
  );
  const hasRenderableNormalizedSeries = chartSeries.some(
    (series) => Array.isArray(series?.data) && series.data.length > 0
  );
  const shouldRenderChart = isAnswerOptionFilteredChart
    ? hasRenderableNormalizedSeries && !hideChart
    : Boolean(activeChart) && !hideChart; // standard charts render whenever chart data is present
  const shouldShowAnswerOptionEmptyState =
    isAnswerOptionFilteredChart &&
    !hideChart &&
    !selectedAnswerOptions.length &&
    !hasRenderableNormalizedSeries;

  useEffect(() => {
    if (turfData?.segments?.length && !selectedSegmentId) {
      setSelectedSegmentId(turfData.segments[0].id);
    }
  }, [turfData?.segments, selectedSegmentId]);

  const questionImage =
    question?.showConceptImage || conceptImages?.length > 0 ? null : question?.image;
  const shouldShowConceptImages = conceptImages?.length > 0 && onOpenConceptImage !== undefined;

  const top2BoxButton = [
    <OverlayTriggerTooltip content="Top 2 Box" key="top-2-box-btn">
      <Button
        onClick={() => {
          setIsTop2BoxModalActive(true);
        }}
        text="Top 2 Box"
        variant="secondary"
      />
    </OverlayTriggerTooltip>,
  ];

  return (
    <Layout.Flex.Column className={styles['comparison-charts']} gap="x-large">
      {renderKeyFindings && renderKeyFindings({ question })}
      {shouldShowConceptImages && (
        <ConceptImageList items={conceptImages} onOpen={onOpenConceptImage} />
      )}
      {questionImage?.cdnUrl && onOpenLightbox && (
        <button
          aria-label={`Open image for ${question.title || question.text || 'question'}`}
          className={styles['comparison-charts-question-image-button']}
          onClick={() => onOpenLightbox(question.id)}
          type="button"
        >
          <img
            alt={question.title || question.text || 'Question image'}
            className={styles['comparison-charts-question-image']}
            src={questionImage.cdnUrl}
          />
        </button>
      )}
      {turfData && (
        <div className={styles['comparison-charts-analysis']}>
          <div className={styles['comparison-charts-analysis-container']}>
            <label className={styles['comparison-charts-analysis-select-label']}>Analysis</label>
            <OverlayTriggerTooltip content="Help">
              <a
                aria-label="Help"
                data-testid="help-link-button"
                href="https://help.insights.numerator.com/en/articles/12333486-in-platform-survey-types-about-turf"
                rel="noreferrer"
                target="_blank"
              >
                <HelpIcon />
              </a>
            </OverlayTriggerTooltip>
          </div>
          <div className={styles['comparison-charts-analysis-actions']}>
            <Select
              className={styles['comparison-charts-analysis-select']}
              onChange={(opt) => setIsTurfAnalysis(Boolean(opt?.value))}
              options={analysisOptions}
              value={selectedAnalysisOption}
            />
            <Button
              icon={<DownloadIcon />}
              onClick={() => setIsDownloadBestSetsModalOpen(true)}
              text="Download Best Sets"
              variant="secondary"
            />
            <ModalDownloadBestSets
              exportQuery={exportQuery}
              globalFilters={globalFilters}
              hasCloseIcon
              isActive={isDownloadBestSetsModalOpen}
              onClose={() => setIsDownloadBestSetsModalOpen(false)}
              survey={survey}
              surveyQuestions={[question]}
            />
          </div>
          <hr />
        </div>
      )}

      {isTurfAnalysis && turfData?.segments?.length > 1 && (
        <Tabs
          aria-label="TURF segment"
          onChange={setSelectedSegmentId}
          options={turfData.segments.map(({ id, name }) => ({ label: name, value: id }))}
          value={selectedSegmentId}
        />
      )}

      {shouldShowAnswerOptionEmptyState && (
        <div
          className={styles['comparison-charts-empty-message']}
          data-testid="answer-options-chart-empty-message"
        >
          Select at least one answer option to view the chart.
        </div>
      )}

      {shouldRenderChart && (
        <BarChart
          chartTitle={
            isTurfAnalysis
              ? `Cumulative Reach${selectedSegment?.name ? ` – ${selectedSegment.name}` : ''}`
              : `Answer Options by ${
                  displayAs === DISPLAY_AS.PERCENT.value
                    ? DISPLAY_AS.PERCENT.label
                    : DISPLAY_AS.COUNT.label
                }`
          }
          colors={colors}
          exportName={`${question?.title || 'chart'}${
            isTurfAnalysis
              ? ` - TURF${selectedSegment?.name ? ` - ${selectedSegment.name}` : ''}`
              : ''
          }`}
          key={`${isTurfAnalysis ? 'turf' : 'std'}-${selectedSegmentId ?? 'all'}-${displayAs}`}
          plotOptions={chartPlotOptions}
          series={chartSeries}
          tooltip={{
            formatter: function () {
              const context = this;
              const tooltipData =
                sortOrder && sortOrder !== 'default' && context.point?.name
                  ? {
                      ...context,
                      category: context.point.name,
                    }
                  : context;
              return tooltipFormatter({
                data: tooltipData,
                format: normalizedActiveChart?.format,
              });
            },
          }}
          xAxis={chartXAxis}
          yAxis={{
            labels: {
              formatter: function () {
                return formatData({ format: normalizedActiveChart?.format, value: this.value });
              },
            },
          }}
        />
      )}

      {shouldRenderChart && activeTable && <hr />}
      {activeTable && (
        <OptionImageTable
          actionButtons={[
            ...(significanceAvailable
              ? [
                  <SignificanceControls
                    alpha={significanceAlpha}
                    inputId={`show-significance-${question.id}`}
                    key="significance-controls"
                    onChange={handleSignificanceChange}
                  />,
                ]
              : []),
            ...(canTop2Box ? top2BoxButton : []),
          ]}
          columns={tableColumns}
          compareByGroups={compareByGroups}
          data={activeTable?.data}
          questionOptions={questionOptions}
        />
      )}
      {significance &&
        (significance.monadic ? (
          <p className={styles['significance-legend']}>
            A letter and green highlight mark a segment significantly higher than the lettered
            segment(s) for that answer option and concept at{' '}
            {Math.round((1 - significanceAlpha) * 100)}% confidence. Segments with fewer than{' '}
            {statThresholds.lowBaseSize} respondents aren&apos;t tested.
          </p>
        ) : (
          <p className={styles['significance-legend']}>
            A letter and green highlight mark a column significantly higher than the lettered
            column(s) at {Math.round((1 - significanceAlpha) * 100)}% confidence. Columns with fewer
            than {statThresholds.lowBaseSize} respondents aren&apos;t tested.
          </p>
        ))}

      <ModalTop2Box
        isActive={isTop2BoxModalActive}
        language={language}
        onApplied={onTop2BoxApplied}
        onClose={() => setIsTop2BoxModalActive(false)}
        question={question}
        surveyId={survey?.id}
      />

      {!isTurfAnalysis && data?.tableOther?.data?.length > 0 && (
        <>
          <hr />
          <OtherPleaseSpecifyTable columns={data.tableOther.columns} data={data.tableOther.data} />
        </>
      )}
    </Layout.Flex.Column>
  );
};

ComparisonCharts.propTypes = {
  compareByGroups: PropTypes.arrayOf(PropTypes.object),
  compareByValue: PropTypes.string,
  conceptImages: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.object,
  displayAs: PropTypes.string,
  exportQuery: PropTypes.object.isRequired,
  globalFilters: PropTypes.object,
  hideChart: PropTypes.bool,
  /** Seed for the per-question alpha state; `undefined` = significance off. */
  initialSignificanceAlpha: PropTypes.number,
  isAnswerOptionFilteredChart: PropTypes.bool,
  isAskWhySurvey: PropTypes.bool,
  language: PropTypes.string,
  onOpenConceptImage: PropTypes.func,
  onOpenLightbox: PropTypes.func,
  /** Called with the new alpha (or `null` when disabled) so it can be persisted. */
  onSignificanceChange: PropTypes.func,
  onTop2BoxApplied: PropTypes.func,
  question: PropTypes.object.isRequired,
  questionOptions: PropTypes.arrayOf(PropTypes.object),
  renderKeyFindings: PropTypes.func,
  selectedAnswerOptions: PropTypes.arrayOf(PropTypes.string),
  /** Whether significance can be offered here (gate on, >1 segment, has options). */
  significanceAvailable: PropTypes.bool,
  sortByValue: PropTypes.string,
  sortOrder: PropTypes.oneOf(['default', 'asc', 'desc']),
  survey: PropTypes.object,
};

export default ComparisonCharts;
